// src/UploadPage/StoreActions/useStoreData.js
import { useState } from 'react';
import { auth } from '../../../Config/firebase.js';

const useStoreData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Save individual store to database
  const saveStore = async (store) => {
    try {
      console.log('💾 SAVING STORE:', store.name);
      
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);
      
      // Save to single stores collection
      const storeDocPath = `projects/xyronis-8cc09/databases/(default)/documents/stores/${store.id}`;
      const storeDocRef = `https://firestore.googleapis.com/v1/${storeDocPath}`;
      
      const response = await fetch(storeDocRef, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          name: storeDocPath,
          fields: {
            id: { stringValue: store.id },
            name: { stringValue: store.name },
            ownerId: { stringValue: user.uid },
            shareLink: { stringValue: store.shareLink },
            items: {
              arrayValue: {
                values: (store.items || []).map(item => ({
                  mapValue: {
                    fields: {
                      id: { stringValue: item.id },
                      itemName: { stringValue: item.itemName },
                      glbUrl: { stringValue: item.glbUrl },
                      imageUrl: item.imageUrl ? { stringValue: item.imageUrl } : { nullValue: null },
                      timestamp: { stringValue: item.timestamp },
                      category: { stringValue: item.category || 'Furniture' },
                    }
                  }
                }))
              }
            },
            createdAt: { stringValue: store.createdAt || new Date().toISOString() },
            updatedAt: { stringValue: new Date().toISOString() }
          }
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Save failed: ${errorText}`);
      }
      
      console.log('✅ Store saved successfully');
      return true;
    } catch (error) {
      console.error('❌ Store save failed:', error);
      setError('Failed to save store: ' + error.message);
      throw error;
    }
  };

  // Load user's stores from database
  const loadUserStores = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const user = auth.currentUser;
      if (!user) {
        console.log('❌ No user logged in');
        return [];
      }
      
      const idToken = await user.getIdToken(true);
      console.log('🔍 Loading stores for user:', user.uid);
      
      // Get all stores, then filter client-side
      const url = `https://firestore.googleapis.com/v1/projects/xyronis-8cc09/databases/(default)/documents/stores`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Fetch failed:', response.status, errorText);
        throw new Error(`Failed to load stores: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📥 Raw response:', data);
      
      // Filter stores by ownerId client-side
      const allStores = (data.documents || []).map(doc => {
        const fields = doc.fields;
        return {
          id: fields.id?.stringValue,
          name: fields.name?.stringValue,
          ownerId: fields.ownerId?.stringValue,
          shareLink: fields.shareLink?.stringValue,
          items: (fields.items?.arrayValue?.values || []).map(itemValue => {
            const itemFields = itemValue.mapValue.fields;
            return {
              id: itemFields.id?.stringValue,
              itemName: itemFields.itemName?.stringValue,
              glbUrl: itemFields.glbUrl?.stringValue,
              imageUrl: itemFields.imageUrl?.stringValue || null,
              timestamp: itemFields.timestamp?.stringValue,
              category: itemFields.category?.stringValue || 'Furniture',
            };
          }),
          createdAt: fields.createdAt?.stringValue,
          updatedAt: fields.updatedAt?.stringValue,
        };
      }).filter(store => store.ownerId === user.uid); // Filter by current user
      
      console.log(`✅ Loaded ${allStores.length} stores for user ${user.uid}`);
      return allStores;
    } catch (error) {
      console.error('❌ Failed to load stores:', error);
      setError('Failed to load stores: ' + error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Load store by ID (for public access via share link)
  const loadStoreById = async (storeId) => {
    try {
      const storeDocRef = `https://firestore.googleapis.com/v1/projects/xyronis-8cc09/databases/(default)/documents/stores/${storeId}`;
      
      const response = await fetch(storeDocRef, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Store not found');
      }
      
      const doc = await response.json();
      const fields = doc.fields;
      
      return {
        id: fields.id.stringValue,
        name: fields.name.stringValue,
        shareLink: fields.shareLink.stringValue,
        ownerId: fields.ownerId.stringValue,
        items: (fields.items?.arrayValue?.values || []).map(itemValue => {
          const itemFields = itemValue.mapValue.fields;
          return {
            id: itemFields.id.stringValue,
            itemName: itemFields.itemName.stringValue,
            glbUrl: itemFields.glbUrl.stringValue,
            imageUrl: itemFields.imageUrl?.stringValue || null,
            timestamp: itemFields.timestamp.stringValue,
            category: itemFields.category?.stringValue || 'Furniture',
          };
        }),
        createdAt: fields.createdAt?.stringValue,
        updatedAt: fields.updatedAt?.stringValue,
      };
    } catch (error) {
      console.error('❌ Failed to load store:', error);
      throw error;
    }
  };

  // Delete store from database
  const deleteStore = async (storeId) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);
      
      // Delete from stores collection
      const storeDocPath = `https://firestore.googleapis.com/v1/projects/xyronis-8cc09/databases/(default)/documents/stores/${storeId}`;
      
      const response = await fetch(storeDocPath, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete store');
      }
      
      console.log('✅ Store deleted successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to remove store:', error);
      setError('Failed to remove store: ' + error.message);
      throw error;
    }
  };

  return {
    // State
    loading,
    error,
    setError,
    
    // Database operations
    saveStore,
    loadUserStores,
    loadStoreById,
    deleteStore,
  };
};

export default useStoreData;